import type {
    CompRef,
    DependenciesBuilder,
    Namespace,
    Dependencies,
    ReferencesMap,
    ReferencesManager,
    DependenciesManager,
    ReferencesBuilder,
    Pointer,
    ReferencedItem
} from '@wix/document-services-types'
import {
    ExtendedRefInfo,
    namespaceToReferencedMap,
    referencedMapToNamespace,
    STRUCTURE_TYPE
} from '@wix/import-export-utils'
import {stripHashIfExists} from '../../../../../utils/refArrayUtils'
import {generateItemIdByNamespace, withHash} from '../../utils'
import _ from 'lodash'
import {CreateExtArgs, pointerUtils} from '@wix/document-manager-core'
import type {DataExtensionAPI} from '../../../../data'

const getReferencedMap = (namespace: Namespace): string => namespaceToReferencedMap[namespace] ?? namespace
const getReferencePointer = (id: string, referencedMap: string): Pointer =>
    pointerUtils.getPointer(id, referencedMapToNamespace[referencedMap] ?? referencedMap)

export const getReferencesManager = (original: ReferencesMap = {}): ReferencesManager => {
    const references: ReferencesMap = original

    const addReference = (id: string, namespaceOrReferencedMap: string, data?: Partial<ReferencedItem>): void => {
        const referencedMap = getReferencedMap(namespaceOrReferencedMap)
        const referenceItem: ReferencedItem = Object.assign({}, {target: id}, _.omitBy(data, _.isUndefined))

        references[referencedMap] ??= {}
        references[referencedMap][id] = referenceItem
    }

    const getTarget = (id: string, referencedMap: string): string | undefined => references[referencedMap]?.[id]?.target

    const getNewItemId = (itemId: string | undefined, namespace: string, originalItemId?: string): string => {
        if (!itemId) {
            return originalItemId ?? generateItemIdByNamespace(namespace)
        }

        let id = getTarget(itemId, namespace)
        if (!id) {
            id = originalItemId ?? generateItemIdByNamespace(namespace)
            addReference(itemId, namespace, {target: id})
        }

        return id
    }

    const getNewItemRef = (originalId: string | undefined, refFieldInfo: ExtendedRefInfo): string => {
        const {referencedMap, noHash, constantValues} = refFieldInfo

        if (originalId) {
            originalId = stripHashIfExists(originalId)

            if (constantValues?.has(originalId)) {
                return originalId
            }
        }

        const id = getNewItemId(originalId, referencedMap)
        if (!noHash) {
            return withHash(id)
        }

        return id
    }

    return {
        addReference,
        getTarget,
        getNewItemId,
        getNewItemRef,
        getReferences: (): ReferencesMap => references
    }
}

const buildDependencies = (references: ReferencesMap): Dependencies | undefined => {
    const dependencies: Dependencies = {references}

    const cleanedDependencies: Partial<Dependencies> = _.omitBy<Dependencies>(
        dependencies,
        val => _.isUndefined(val) || _.keys(val).length === 0
    )
    if (Object.keys(cleanedDependencies).length === 0) {
        return undefined
    }

    return cleanedDependencies
}

export const getDependenciesManager = (original: Dependencies = {}): DependenciesManager => {
    const references: ReferencesManager = getReferencesManager(original?.references)

    return {
        references,
        getDependencies: (): Dependencies | undefined => buildDependencies(references.getReferences())
    }
}

type RefsMap = Record<Namespace, Set<string>>

const getReferencesBuilder = ({extensionAPI}: CreateExtArgs, components: CompRef[]): ReferencesBuilder => {
    const nonOwnedRefs: RefsMap = {}
    const internalRefs: RefsMap = {
        [STRUCTURE_TYPE]: new Set(components.map(comp => comp.id))
    }

    const hasInternalRef = (ref: string, namespace: Namespace): boolean => internalRefs[namespace]?.has(ref) ?? false

    const addRef = (refMap: RefsMap, ref: string, namespace: Namespace): void => {
        refMap[namespace] ??= new Set()
        refMap[namespace].add(ref)
    }

    const buildReferenceMap = (): ReferencesMap => {
        const {data} = extensionAPI as DataExtensionAPI
        const referencesManager = getReferencesManager()

        for (const [namespace, refs] of Object.entries(nonOwnedRefs)) {
            for (const ref of refs) {
                if (!hasInternalRef(ref, namespace)) {
                    const type = data.getItemType(getReferencePointer(ref, namespace))
                    referencesManager.addReference(ref, namespace, {type})
                }
            }
        }

        return referencesManager.getReferences()
    }

    return {
        addInternalRef: (ref: string, namespace: Namespace): void => addRef(internalRefs, ref, namespace),
        addNonOwnedRef: (ref: string, namespace: Namespace): void => addRef(nonOwnedRefs, ref, namespace),
        hasInternalRef,
        build: buildReferenceMap
    }
}

export const getDependenciesBuilder = (
    extensionContext: CreateExtArgs,
    components: CompRef[] = []
): DependenciesBuilder => {
    const references: ReferencesBuilder = getReferencesBuilder(extensionContext, components)

    return {
        references,
        build: (): Dependencies | undefined => buildDependencies(references.build())
    }
}
